// Admin page for the email agent evaluation harness.
// Manages truth-set cases, triggers runs, and displays results.

import { Fragment, useState, useEffect, useCallback } from 'react';
import {
  emailEvalService,
  type TruthCase,
  type TruthCaseCreatePayload,
  type EvalRun,
  type EvalRunKind,
  type CaseResult,
  type QuickRunResponse,
} from '../services/emailEval';
import { formatGitSha } from '../utils/gitShaDisplay';
import {
  canSpotCheck,
  passRate,
  runKindBadge,
  runProvenance,
  verdictBadge,
  type Badge,
} from './emailEvalRunHelpers';

// ---------------------------------------------------------------------------
// Styles (inline CSS modules pattern matching existing admin pages)
// ---------------------------------------------------------------------------

const styles = {
  page: {
    padding: '24px 32px',
    maxWidth: 1400,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  } as React.CSSProperties,
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  } as React.CSSProperties,
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    margin: '4px 0 0',
  } as React.CSSProperties,
  tabs: {
    display: 'flex',
    gap: 0,
    borderBottom: '1px solid #e5e7eb',
    marginBottom: 24,
  } as React.CSSProperties,
  tab: (active: boolean) => ({
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? '#2563eb' : '#6b7280',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s',
  } as React.CSSProperties),
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 13,
  },
  th: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderBottom: '2px solid #e5e7eb',
    color: '#374151',
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    verticalAlign: 'top' as const,
  },
  tag: (color: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    backgroundColor: color + '15',
    color: color,
    marginRight: 4,
    marginBottom: 2,
  } as React.CSSProperties),
  badge: (ok: boolean) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: ok ? '#dcfce7' : '#fef2f2',
    color: ok ? '#166534' : '#991b1b',
  } as React.CSSProperties),
  statusBadge: (status: string) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor:
      status === 'completed' ? '#dcfce7' :
      status === 'running' ? '#dbeafe' :
      '#fef2f2',
    color:
      status === 'completed' ? '#166534' :
      status === 'running' ? '#1e40af' :
      '#991b1b',
  } as React.CSSProperties),
  btn: (variant: 'primary' | 'secondary' | 'danger' = 'primary') => ({
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    border: variant === 'primary' ? 'none' : '1px solid #d1d5db',
    backgroundColor:
      variant === 'primary' ? '#2563eb' :
      variant === 'danger' ? '#ef4444' :
      '#fff',
    color: variant === 'primary' || variant === 'danger' ? '#fff' : '#374151',
    transition: 'all 0.15s',
  } as React.CSSProperties),
  btnSmall: {
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
  } as React.CSSProperties,
  summaryCard: {
    display: 'flex',
    gap: 16,
    marginBottom: 20,
  } as React.CSSProperties,
  summaryItem: (color: string) => ({
    flex: 1,
    padding: 16,
    borderRadius: 8,
    border: `1px solid ${color}30`,
    backgroundColor: `${color}08`,
  } as React.CSSProperties),
  summaryValue: {
    fontSize: 28,
    fontWeight: 700,
    margin: 0,
  } as React.CSSProperties,
  summaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    margin: '4px 0 0',
  } as React.CSSProperties,
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    maxWidth: 700,
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  } as React.CSSProperties,
  emptyState: {
    textAlign: 'center' as const,
    padding: 40,
    color: '#9ca3af',
  },
  searchInput: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    width: 280,
    outline: 'none',
  } as React.CSSProperties,
  expandedRow: {
    backgroundColor: '#f9fafb',
    padding: 16,
    fontSize: 13,
    lineHeight: 1.6,
    color: '#4b5563',
  } as React.CSSProperties,
};

// Tag color mapping
const TAG_COLORS: Record<string, string> = {
  clean: '#6b7280',
  internal_forward: '#8b5cf6',
  ocr: '#f59e0b',
  sku_mapping: '#ef4444',
  whitecap: '#3b82f6',
  positive_control: '#10b981',
  regression: '#ef4444',
  needs_review: '#f59e0b',
  private_label: '#ec4899',
  sender_relay: '#8b5cf6',
  quantity_self_check: '#f59e0b',
  portal_order: '#6366f1',
  ariba: '#14b8a6',
  date_bleed: '#f59e0b',
  negative_control: '#6b7280',
};

function getTagColor(tag: string): string {
  return TAG_COLORS[tag] || '#6b7280';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Case Editor modal — used for both Create and Edit flows.
// ---------------------------------------------------------------------------

interface CaseEditorProps {
  /** null = create mode; a case_id = edit mode */
  caseId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_CASE: TruthCaseCreatePayload = {
  case_id: '',
  description: '',
  email_subject: '',
  email_from: '',
  email_body: '',
  stage1_matched_customer_no: null,
  stage1_matched_customer_name: null,
  stage1_sender_is_internal: null,
  expected_behavior: '',
  expected_category: 'order',
  expected_customer_no: null,
  expected_line_items: [],
  expected_ship_to_code: null,
  tags: [],
  notes: '',
  is_active: true,
};

function CaseEditor({ caseId, onClose, onSaved }: CaseEditorProps) {
  const isEdit = caseId !== null;
  const [form, setForm] = useState<TruthCaseCreatePayload>(EMPTY_CASE);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagsText, setTagsText] = useState('');
  const [lineItemsJson, setLineItemsJson] = useState('[]');

  useEffect(() => {
    if (!isEdit || caseId === null) return;
    emailEvalService
      .getCase(caseId)
      .then((detail) => {
        setForm({
          case_id: detail.case_id,
          description: detail.description,
          email_subject: detail.email_subject,
          email_from: detail.email_from,
          email_body: detail.email_body,
          stage1_matched_customer_no: detail.stage1_matched_customer_no,
          stage1_matched_customer_name: detail.stage1_matched_customer_name,
          stage1_sender_is_internal: detail.stage1_sender_is_internal,
          expected_behavior: detail.expected_behavior,
          expected_category: detail.expected_category,
          expected_customer_no: detail.expected_customer_no,
          expected_line_items: detail.expected_line_items,
          expected_ship_to_code: detail.expected_ship_to_code,
          tags: detail.tags,
          notes: detail.notes,
          is_active: detail.is_active,
        });
        setTagsText(detail.tags.join(', '));
        setLineItemsJson(JSON.stringify(detail.expected_line_items, null, 2));
      })
      .catch((e) => setError(String(e?.response?.data?.detail ?? e)))
      .finally(() => setLoading(false));
  }, [caseId, isEdit]);

  const update = <K extends keyof TruthCaseCreatePayload>(
    key: K,
    value: TruthCaseCreatePayload[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setError(null);
    setSaving(true);
    try {
      // Parse tags + line_items from the textarea inputs.
      const tags = tagsText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      let parsedItems: TruthCaseCreatePayload['expected_line_items'] = [];
      try {
        parsedItems = JSON.parse(lineItemsJson);
        if (!Array.isArray(parsedItems)) throw new Error('must be a JSON array');
      } catch (e) {
        setError(`expected_line_items is not valid JSON: ${String(e)}`);
        setSaving(false);
        return;
      }
      const payload: TruthCaseCreatePayload = {
        ...form,
        tags,
        expected_line_items: parsedItems,
      };
      if (isEdit && caseId) {
        // For edit, strip case_id (immutable on the server).
        const updatePayload: Partial<TruthCaseCreatePayload> = { ...payload };
        delete updatePayload.case_id;
        await emailEvalService.updateCase(caseId, updatePayload);
      } else {
        await emailEvalService.createCase(payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(String(err?.response?.data?.detail ?? e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.modal} onClick={onClose}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          Loading case...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>
          {isEdit ? `Edit case: ${caseId}` : 'New truth case'}
        </h3>
        {error && (
          <div
            style={{
              padding: 10,
              marginBottom: 12,
              background: '#fef2f2',
              color: '#991b1b',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 13 }}>
            Case ID
            <input
              value={form.case_id}
              disabled={isEdit}
              onChange={(e) => update('case_id', e.target.value)}
              style={fieldStyle}
              placeholder="e.g. AU00185 or SLUG_NAME"
            />
          </label>
          <label style={{ fontSize: 13 }}>
            Expected category
            <select
              value={form.expected_category ?? 'order'}
              onChange={(e) => update('expected_category', e.target.value)}
              style={fieldStyle}
            >
              <option value="order">order</option>
              <option value="quote">quote</option>
              <option value="inquiry">inquiry</option>
              <option value="other">other</option>
            </select>
          </label>
        </div>

        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
          Description
          <input
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            style={fieldStyle}
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 13 }}>
            Email from
            <input
              value={form.email_from}
              onChange={(e) => update('email_from', e.target.value)}
              style={fieldStyle}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            Email subject
            <input
              value={form.email_subject}
              onChange={(e) => update('email_subject', e.target.value)}
              style={fieldStyle}
            />
          </label>
        </div>

        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
          Email body
          <textarea
            value={form.email_body}
            onChange={(e) => update('email_body', e.target.value)}
            style={{ ...fieldStyle, minHeight: 120, fontFamily: 'monospace' }}
          />
        </label>

        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
          Expected behavior (judge rubric)
          <textarea
            value={form.expected_behavior}
            onChange={(e) => update('expected_behavior', e.target.value)}
            style={{ ...fieldStyle, minHeight: 80 }}
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 13 }}>
            Expected customer #
            <input
              value={form.expected_customer_no ?? ''}
              onChange={(e) =>
                update('expected_customer_no', e.target.value || null)
              }
              style={fieldStyle}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            Expected ship-to code
            <input
              value={form.expected_ship_to_code ?? ''}
              onChange={(e) =>
                update('expected_ship_to_code', e.target.value || null)
              }
              style={fieldStyle}
            />
          </label>
        </div>

        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
          Expected line items (JSON array)
          <textarea
            value={lineItemsJson}
            onChange={(e) => setLineItemsJson(e.target.value)}
            style={{ ...fieldStyle, minHeight: 80, fontFamily: 'monospace' }}
            placeholder='[{"item_code":"F2008.55","quantity":4,"unit_price":null}]'
          />
        </label>

        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
          Tags (comma-separated)
          <input
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            style={fieldStyle}
            placeholder="order, happy_path, regression"
          />
        </label>

        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
          Notes
          <textarea
            value={form.notes ?? ''}
            onChange={(e) => update('notes', e.target.value)}
            style={{ ...fieldStyle, minHeight: 60 }}
          />
        </label>

        {isEdit && (
          <label
            style={{
              fontSize: 13,
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <input
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => update('is_active', e.target.checked)}
            />
            Active (unchecked = soft-deleted, excluded from runs)
          </label>
        )}

        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            marginTop: 20,
          }}
        >
          <button style={styles.btn('secondary')} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button style={styles.btn('primary')} onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create case'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '6px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};

interface CasesFetch {
  /** The fetchKey this list was loaded for. */
  key: number;
  cases: TruthCase[];
}

function CasesTab() {
  // The list is stored together with the fetch key it was loaded for, and
  // `refresh` just bumps the key. While the stored key trails the current one
  // the render shows the loading state on its own, so nothing has to be reset
  // synchronously inside the effect (react-hooks/set-state-in-effect), and a
  // late response for a superseded fetch can no longer overwrite the newer one.
  const [fetchKey, setFetchKey] = useState(0);
  const [fetched, setFetched] = useState<CasesFetch | null>(null);
  const loading = fetched?.key !== fetchKey;
  const cases = fetched?.cases ?? [];
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editorCaseId, setEditorCaseId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const refresh = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    const key = fetchKey;
    emailEvalService.listCases().then(
      (res) => {
        if (!cancelled) setFetched({ key, cases: res.cases });
      },
      () => {
        // A failed reload ends the loading state and keeps the last list.
        if (!cancelled) setFetched((prev) => ({ key, cases: prev?.cases ?? [] }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [fetchKey]);

  const onDelete = async (caseId: string) => {
    if (!window.confirm(`Soft-delete case ${caseId}? (it will be excluded from runs)`)) {
      return;
    }
    await emailEvalService.deleteCase(caseId);
    refresh();
  };

  const filtered = cases.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.id.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q) ||
      c.email_subject.toLowerCase().includes(q) ||
      c.tags.some((t) => t.toLowerCase().includes(q))
    );
  });

  if (loading) return <div style={styles.emptyState}>Loading cases...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
        <input
          style={styles.searchInput}
          placeholder="Search cases by ID, description, or tag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {filtered.length} of {cases.length} cases
          </span>
          <button
            style={styles.btn('primary')}
            onClick={() => {
              setEditorCaseId(null);
              setEditorOpen(true);
            }}
          >
            + New case
          </button>
        </div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Case ID</th>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Customer</th>
            <th style={styles.th}>Tags</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c) => (
            <Fragment key={c.id}>
              <tr
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
              >
                <td style={{ ...styles.td, fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
                  {c.id}
                </td>
                <td style={{ ...styles.td, maxWidth: 350 }}>
                  {c.description.slice(0, 120)}{c.description.length > 120 ? '...' : ''}
                </td>
                <td style={styles.td}>{c.expected_category}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>
                  {c.expected_customer_no || '—'}
                </td>
                <td style={styles.td}>
                  {c.tags.map((t) => (
                    <span key={t} style={styles.tag(getTagColor(t))}>{t}</span>
                  ))}
                </td>
                <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                  <button
                    style={{ ...styles.btnSmall, marginRight: 4 }}
                    onClick={() => {
                      setEditorCaseId(c.id);
                      setEditorOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    style={{ ...styles.btnSmall, color: '#991b1b' }}
                    onClick={() => onDelete(c.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
              {expandedId === c.id && (
                <tr>
                  <td colSpan={6} style={styles.expandedRow}>
                    <div style={{ marginBottom: 8 }}>
                      <strong>From:</strong> {c.email_from}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Subject:</strong> {c.email_subject}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Expected behavior:</strong>
                      <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{c.expected_behavior}</div>
                    </div>
                    {c.has_line_items && (
                      <div><strong>Has expected line items</strong></div>
                    )}
                    {c.notes && (
                      <div style={{ marginTop: 8 }}>
                        <strong>Notes:</strong> {c.notes}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      {editorOpen && (
        <CaseEditor
          caseId={editorCaseId}
          onClose={() => setEditorOpen(false)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Quick-Run Tab — fire an ad-hoc email through the agent without saving a
// case.  Handy for "I saw a failing email in prod — what does the agent do
// with it right now?" investigation flows.
// ---------------------------------------------------------------------------

function QuickRunTab() {
  const [form, setForm] = useState({
    email_subject: '',
    email_from: '',
    email_body: '',
    stage1_matched_customer_no: '',
    stage1_matched_customer_name: '',
    stage1_sender_is_internal: '' as '' | 'true' | 'false',
    expected_behavior: '',
    expected_category: '',
    expected_customer_no: '',
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<QuickRunResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const resp = await emailEvalService.quickRun({
        email_subject: form.email_subject,
        email_from: form.email_from,
        email_body: form.email_body,
        stage1_matched_customer_no: form.stage1_matched_customer_no || null,
        stage1_matched_customer_name: form.stage1_matched_customer_name || null,
        stage1_sender_is_internal:
          form.stage1_sender_is_internal === ''
            ? null
            : form.stage1_sender_is_internal === 'true',
        expected_behavior: form.expected_behavior || null,
        expected_category: form.expected_category || null,
        expected_customer_no: form.expected_customer_no || null,
      });
      setResult(resp);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(String(err?.response?.data?.detail ?? e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
        Run the current email agent pipeline against an ad-hoc email without
        saving a case. Provide <code>expected_behavior</code> to also get an
        LLM judge verdict.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label style={{ fontSize: 13 }}>
          Email from
          <input
            value={form.email_from}
            onChange={(e) => setForm({ ...form, email_from: e.target.value })}
            style={fieldStyle}
            placeholder="buyer@customer.com"
          />
        </label>
        <label style={{ fontSize: 13 }}>
          Email subject
          <input
            value={form.email_subject}
            onChange={(e) => setForm({ ...form, email_subject: e.target.value })}
            style={fieldStyle}
            placeholder="Order — PO# 12345"
          />
        </label>
      </div>

      <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
        Email body
        <textarea
          value={form.email_body}
          onChange={(e) => setForm({ ...form, email_body: e.target.value })}
          style={{ ...fieldStyle, minHeight: 160, fontFamily: 'monospace' }}
          placeholder="Paste the raw email body (plus any attachment text you have)…"
        />
      </label>

      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: '#374151' }}>
          Stage-1 overrides (optional)
        </summary>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 12,
            marginTop: 8,
          }}
        >
          <label style={{ fontSize: 13 }}>
            Matched customer #
            <input
              value={form.stage1_matched_customer_no}
              onChange={(e) =>
                setForm({ ...form, stage1_matched_customer_no: e.target.value })
              }
              style={fieldStyle}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            Matched customer name
            <input
              value={form.stage1_matched_customer_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  stage1_matched_customer_name: e.target.value,
                })
              }
              style={fieldStyle}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            Sender is internal
            <select
              value={form.stage1_sender_is_internal}
              onChange={(e) =>
                setForm({
                  ...form,
                  stage1_sender_is_internal: e.target.value as '' | 'true' | 'false',
                })
              }
              style={fieldStyle}
            >
              <option value="">(auto)</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </label>
        </div>
      </details>

      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: '#374151' }}>
          Expected behavior (optional — enables judge verdict)
        </summary>
        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
          Expected behavior (judge rubric)
          <textarea
            value={form.expected_behavior}
            onChange={(e) =>
              setForm({ ...form, expected_behavior: e.target.value })
            }
            style={{ ...fieldStyle, minHeight: 60 }}
            placeholder="e.g. Classify as order, extract F2008.55 x 4, customer = CHEMA01"
          />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 13 }}>
            Expected category
            <select
              value={form.expected_category}
              onChange={(e) =>
                setForm({ ...form, expected_category: e.target.value })
              }
              style={fieldStyle}
            >
              <option value="">(any)</option>
              <option value="order">order</option>
              <option value="quote">quote</option>
              <option value="inquiry">inquiry</option>
              <option value="other">other</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            Expected customer #
            <input
              value={form.expected_customer_no}
              onChange={(e) =>
                setForm({ ...form, expected_customer_no: e.target.value })
              }
              style={fieldStyle}
            />
          </label>
        </div>
      </details>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          style={styles.btn('primary')}
          onClick={run}
          disabled={running || !form.email_body || !form.email_from || !form.email_subject}
        >
          {running ? 'Running agent...' : 'Run agent'}
        </button>
      </div>

      {error && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: '#fef2f2',
            color: '#991b1b',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div style={{ ...styles.card, marginTop: 20 }}>
          <h4 style={{ margin: '0 0 12px' }}>Result</h4>
          {result.judge_ok !== null && (
            <div style={{ marginBottom: 12 }}>
              <span style={styles.badge(result.judge_ok)}>
                {result.judge_ok ? 'PASS' : 'FAIL'}
              </span>{' '}
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                (confidence: {result.judge_confidence || '—'})
              </span>
            </div>
          )}
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <div>
              <strong>Actual category:</strong> {result.actual_category ?? '—'}
            </div>
            <div>
              <strong>Actual customer #:</strong>{' '}
              <span style={{ fontFamily: 'monospace' }}>
                {result.actual_customer_no ?? '—'}
              </span>
            </div>
            <div>
              <strong>Latency:</strong> {result.latency_ms}ms
            </div>
            {result.error && (
              <div style={{ color: '#991b1b' }}>
                <strong>Error:</strong> {result.error}
              </div>
            )}
            {result.judge_reasoning && (
              <div style={{ marginTop: 8 }}>
                <strong>Judge reasoning:</strong>
                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
                  {result.judge_reasoning}
                </div>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <strong>Line items ({result.actual_line_items.length}):</strong>
              <pre
                style={{
                  marginTop: 4,
                  background: '#f9fafb',
                  padding: 10,
                  borderRadius: 4,
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 240,
                }}
              >
                {JSON.stringify(result.actual_line_items, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Code revision a run was recorded with. A run started on uncommitted
// working-tree code is flagged, since it did not execute that commit.
function GitShaLabel({ value, length }: { value: string; length: number }) {
  const { short, dirty } = formatGitSha(value, length);
  return (
    <span title={dirty ? `${value} (run on uncommitted working-tree changes)` : value}>
      {short}
      {dirty && (
        <span
          data-testid="git-sha-dirty"
          style={{
            marginLeft: 6,
            padding: '1px 6px',
            borderRadius: 4,
            background: '#fef3c7',
            color: '#92400e',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          dirty
        </span>
      )}
    </span>
  );
}

type RunKindFilter = 'all' | EvalRunKind;

function badgeStyle(b: Badge): React.CSSProperties {
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: b.bg,
    color: b.fg,
    whiteSpace: 'nowrap',
  };
}

export function RunsTab() {
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState<EvalRun | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [kindFilter, setKindFilter] = useState<RunKindFilter>('all');
  const [spotCheckCase, setSpotCheckCase] = useState<CaseResult | null>(null);
  const [spotCheckOk, setSpotCheckOk] = useState(true);
  const [spotCheckReason, setSpotCheckReason] = useState('');

  const loadRuns = useCallback(async () => {
    try {
      const res = await emailEvalService.listRuns(
        kindFilter === 'all' ? undefined : kindFilter,
      );
      setRuns(res.runs);
    } finally {
      setLoading(false);
    }
  }, [kindFilter]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  // Poll for running runs
  useEffect(() => {
    const hasRunning = runs.some((r) => r.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(loadRuns, 5000);
    return () => clearInterval(interval);
  }, [runs, loadRuns]);

  const triggerFullRun = async () => {
    setTriggering(true);
    try {
      await emailEvalService.triggerRun();
      await loadRuns();
    } finally {
      setTriggering(false);
    }
  };

  const submitSpotCheck = async () => {
    if (!selectedRun || !spotCheckCase) return;
    const overrides = {
      [spotCheckCase.case_id]: {
        ok: spotCheckOk,
        reason: spotCheckReason || 'Manual override',
      },
    };
    const updated = await emailEvalService.applySpotCheck(
      selectedRun.run_id,
      overrides,
    );
    setSelectedRun(updated);
    // Update in list too
    setRuns((prev) => prev.map((r) => r.run_id === updated.run_id ? updated : r));
    setSpotCheckCase(null);
    setSpotCheckReason('');
  };

  if (loading) return <div style={styles.emptyState}>Loading runs...</div>;

  // Run detail view
  if (selectedRun) {
    return (
      <div>
        <button
          style={styles.btn('secondary')}
          onClick={() => setSelectedRun(null)}
        >
          Back to runs
        </button>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>Run {selectedRun.run_id}</h3>
            <span style={styles.statusBadge(selectedRun.status)}>
              {selectedRun.status}
            </span>
            <span style={badgeStyle(runKindBadge(selectedRun.run_kind))} data-testid="run-kind-badge">
              {runKindBadge(selectedRun.run_kind).label}
            </span>
            {runProvenance(selectedRun) && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>{runProvenance(selectedRun)}</span>
            )}
          </div>

          {selectedRun.pipeline_version && (
            <div style={{ ...styles.card, fontSize: 12, fontFamily: 'monospace', display: 'flex', gap: 24, padding: 12, flexWrap: 'wrap' }}>
              <span><strong>Git:</strong> <GitShaLabel value={selectedRun.pipeline_version.git_sha} length={10} /></span>
              <span><strong>Model:</strong> {selectedRun.pipeline_version.model_id}</span>
              {selectedRun.run_kind !== 'full_pipeline' && (
                <span><strong>Prompt:</strong> {selectedRun.pipeline_version.prompt_hash.slice(0, 8)}</span>
              )}
              <span><strong>Time:</strong> {new Date(selectedRun.pipeline_version.timestamp).toLocaleString()}</span>
              {selectedRun.case_filter && <span><strong>Filter:</strong> {selectedRun.case_filter}</span>}
            </div>
          )}

          {selectedRun.run_kind === 'full_pipeline' && (
            <div style={{ ...styles.card, fontSize: 12, padding: 12, color: '#4b5563' }}>
              Deterministic reconciliation of the draft the whole pipeline persisted on staging
              (Merlin extractor; KB reads go through the answer-leakage gate).{' '}
              <strong>HELD</strong> = wrong or missing lines that were all flagged for review (safe);{' '}
              <strong>FAIL</strong> = a silently-wrong draft.
              {selectedRun.report_uri && (
                <div style={{ marginTop: 6, fontFamily: 'monospace' }}>
                  <strong>Report:</strong> {selectedRun.report_uri}
                </div>
              )}
            </div>
          )}

          {selectedRun.summary.total > 0 && (
            <div style={styles.summaryCard}>
              <div style={styles.summaryItem('#2563eb')}>
                <p style={styles.summaryValue}>{selectedRun.summary.total}</p>
                <p style={styles.summaryLabel}>Total cases</p>
              </div>
              <div style={styles.summaryItem('#10b981')}>
                <p style={{ ...styles.summaryValue, color: '#166534' }}>{selectedRun.summary.passed}</p>
                <p style={styles.summaryLabel}>Passed</p>
              </div>
              {selectedRun.run_kind === 'full_pipeline' && (
                <div style={styles.summaryItem('#f59e0b')}>
                  <p style={{ ...styles.summaryValue, color: '#92400e' }}>{selectedRun.summary.held ?? 0}</p>
                  <p style={styles.summaryLabel}>Held (flagged)</p>
                </div>
              )}
              <div style={styles.summaryItem('#ef4444')}>
                <p style={{ ...styles.summaryValue, color: '#991b1b' }}>{selectedRun.summary.failed}</p>
                <p style={styles.summaryLabel}>Failed</p>
              </div>
              {selectedRun.run_kind === 'full_pipeline' && (
                <div style={styles.summaryItem('#6b7280')}>
                  <p style={{ ...styles.summaryValue, color: '#374151' }}>{selectedRun.summary.no_order ?? 0}</p>
                  <p style={styles.summaryLabel}>No order</p>
                </div>
              )}
              <div style={styles.summaryItem('#8b5cf6')}>
                <p style={styles.summaryValue}>
                  {passRate(selectedRun.summary) ?? 0}%
                </p>
                <p style={styles.summaryLabel}>Pass rate</p>
              </div>
            </div>
          )}

          {selectedRun.status === 'running' && (
            <div style={{ ...styles.emptyState, color: '#2563eb' }}>
              Run in progress... auto-refreshing every 5s
            </div>
          )}

          {selectedRun.results.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Case</th>
                  <th style={styles.th}>Verdict</th>
                  <th style={styles.th}>{selectedRun.run_kind === 'full_pipeline' ? 'Extractor' : 'Confidence'}</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Latency</th>
                  <th style={styles.th}>{selectedRun.run_kind === 'full_pipeline' ? 'Reconciliation' : 'Reasoning'}</th>
                  {canSpotCheck(selectedRun) && <th style={styles.th}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {selectedRun.results.map((r) => (
                  <tr key={r.case_id}>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>
                      {r.case_id}
                    </td>
                    <td style={styles.td}>
                      <span style={badgeStyle(verdictBadge(r, selectedRun.run_kind))} data-testid={`verdict-${r.case_id}`}>
                        {verdictBadge(r, selectedRun.run_kind).label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {selectedRun.run_kind === 'full_pipeline' ? (r.extractor || '—') : r.judge_confidence}
                    </td>
                    <td style={styles.td}>{r.actual_category || '—'}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>
                      {r.actual_customer_no || '—'}
                    </td>
                    <td style={styles.td}>{(r.latency_ms / 1000).toFixed(1)}s</td>
                    <td style={{ ...styles.td, maxWidth: 360, fontSize: 12 }} title={r.reasoning}>
                      {r.reasoning.slice(0, 220)}{r.reasoning.length > 220 ? '...' : ''}
                      {r.error && (
                        <div style={{ color: '#991b1b', marginTop: 4 }}>{r.error.slice(0, 160)}</div>
                      )}
                    </td>
                    {canSpotCheck(selectedRun) && (
                      <td style={styles.td}>
                        <button
                          style={styles.btnSmall}
                          onClick={() => {
                            setSpotCheckCase(r);
                            setSpotCheckOk(!r.ok);
                          }}
                          title="Override this verdict"
                        >
                          Override
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Spot-check modal */}
        {spotCheckCase && (
          <div style={styles.modal} onClick={() => setSpotCheckCase(null)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>
                Override verdict: {spotCheckCase.case_id}
              </h3>
              <div style={{ marginBottom: 12 }}>
                <strong>Current verdict:</strong>{' '}
                <span style={styles.badge(spotCheckCase.ok)}>
                  {spotCheckCase.ok ? 'PASS' : 'FAIL'}
                </span>
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>LLM reasoning:</strong>
                <div style={{ fontSize: 13, color: '#4b5563', marginTop: 4 }}>
                  {spotCheckCase.reasoning}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  New verdict:
                </label>
                <select
                  value={spotCheckOk ? 'pass' : 'fail'}
                  onChange={(e) => setSpotCheckOk(e.target.value === 'pass')}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
                >
                  <option value="pass">PASS</option>
                  <option value="fail">FAIL</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Reason:
                </label>
                <textarea
                  value={spotCheckReason}
                  onChange={(e) => setSpotCheckReason(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 6,
                    border: '1px solid #d1d5db',
                    fontSize: 13,
                    minHeight: 80,
                    resize: 'vertical',
                  }}
                  placeholder="Why are you overriding this verdict?"
                />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button style={styles.btn('secondary')} onClick={() => setSpotCheckCase(null)}>
                  Cancel
                </button>
                <button style={styles.btn('primary')} onClick={submitSpotCheck}>
                  Apply override
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Runs list view
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {runs.length} run(s)
          </span>
          <label style={{ fontSize: 13, color: '#374151', display: 'flex', gap: 6, alignItems: 'center' }}>
            Harness
            <select
              aria-label="Run kind filter"
              value={kindFilter}
              onChange={(e) => { setLoading(true); setKindFilter(e.target.value as RunKindFilter); }}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
            >
              <option value="all">All</option>
              <option value="extraction">Extraction · LLM judge</option>
              <option value="full_pipeline">Full pipeline · Merlin</option>
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={styles.btn('secondary')}
            onClick={loadRuns}
          >
            Refresh
          </button>
          <button
            style={styles.btn('primary')}
            onClick={triggerFullRun}
            disabled={triggering}
            title="Runs the extraction-only (LLM-judged) harness in-process. Full-pipeline Merlin runs come from the weekly GitHub Actions job on staging."
          >
            {triggering ? 'Starting...' : 'Run extraction eval'}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>
        Full-pipeline runs (Merlin extractor, staging, deterministic reconciliation of the persisted draft)
        are produced by the weekly <code>chemmasters_full_pipeline_eval</code> GitHub Actions job and
        ingested here hourly; trigger one manually from the workflow's Run button.
      </p>

      {runs.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={{ fontSize: 16, marginBottom: 8 }}>No runs yet</p>
          <p>Click "Run extraction eval" to execute the truth set against the current pipeline, or wait for the weekly full-pipeline job.</p>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Run ID</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Harness</th>
              <th style={styles.th}>Started</th>
              <th style={styles.th}>Cases</th>
              <th style={styles.th}>Passed</th>
              <th style={styles.th}>Held</th>
              <th style={styles.th}>Failed</th>
              <th style={styles.th}>No order</th>
              <th style={styles.th}>Rate</th>
              <th style={styles.th}>Version</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr
                key={run.run_id}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (run.status === 'running') {
                    // Refresh to get latest state
                    emailEvalService.getRun(run.run_id).then(setSelectedRun);
                  } else {
                    setSelectedRun(run);
                  }
                }}
              >
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>
                  {run.run_id}
                </td>
                <td style={styles.td}>
                  <span style={styles.statusBadge(run.status)}>{run.status}</span>
                </td>
                <td style={styles.td}>
                  <span style={badgeStyle(runKindBadge(run.run_kind))}>{runKindBadge(run.run_kind).label}</span>
                </td>
                <td style={styles.td}>
                  {new Date(run.started_at).toLocaleString()}
                </td>
                <td style={styles.td}>{run.summary.total || '—'}</td>
                <td style={{ ...styles.td, color: '#166534', fontWeight: 600 }}>
                  {run.summary.passed || 0}
                </td>
                <td style={{ ...styles.td, color: '#92400e', fontWeight: 600 }}>
                  {run.run_kind === 'full_pipeline' ? (run.summary.held ?? 0) : '—'}
                </td>
                <td style={{ ...styles.td, color: '#991b1b', fontWeight: 600 }}>
                  {run.summary.failed || 0}
                </td>
                <td style={{ ...styles.td, color: '#374151', fontWeight: 600 }}>
                  {run.run_kind === 'full_pipeline' ? (run.summary.no_order ?? 0) : '—'}
                </td>
                <td style={styles.td}>
                  {passRate(run.summary) === null ? '—' : `${passRate(run.summary)}%`}
                </td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>
                  {run.pipeline_version
                    ? <GitShaLabel value={run.pipeline_version.git_sha} length={7} />
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

type TabId = 'cases' | 'runs' | 'quick_run';

export function EmailEvalPage() {
  const [activeTab, setActiveTab] = useState<TabId>('cases');

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Email Eval Harness</h1>
          <p style={styles.subtitle}>
            Manage truth-set cases and run A/B evaluations of the email agent pipeline
          </p>
        </div>
      </div>

      <div style={styles.tabs}>
        <button
          style={styles.tab(activeTab === 'cases')}
          onClick={() => setActiveTab('cases')}
        >
          Truth Set ({'\u2714'} Cases)
        </button>
        <button
          style={styles.tab(activeTab === 'runs')}
          onClick={() => setActiveTab('runs')}
        >
          Runs & Results
        </button>
        <button
          style={styles.tab(activeTab === 'quick_run')}
          onClick={() => setActiveTab('quick_run')}
        >
          Quick Run
        </button>
      </div>

      <div style={styles.card}>
        {activeTab === 'cases' && <CasesTab />}
        {activeTab === 'runs' && <RunsTab />}
        {activeTab === 'quick_run' && <QuickRunTab />}
      </div>
    </div>
  );
}
